import React, { useState } from 'react';
import { Delivery, mockDrivers } from '../data/mockData';
import { ChevronRight, Search, PackageIcon } from 'lucide-react';
interface DeliveryListProps {
  deliveries: Delivery[];
  onSelectDriver: (driverId: string, deliveryRef?: string) => void;
  selectedDriverId: string | null;
  showFilters?: boolean;
}
export function DeliveryList({
  deliveries,
  onSelectDriver,
  selectedDriverId,
  showFilters = true
}: DeliveryListProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'at-risk' | 'critical' | 'on-track'>('all');
  const criticalDriverIds = new Set(mockDrivers.filter(d => d.status === 'critical').map(d => d.id));
  const statusCounts = {
    all: deliveries.length,
    'at-risk': deliveries.filter(d => d.status === 'at-risk' && !criticalDriverIds.has(d.driverId)).length,
    critical: deliveries.filter(d => criticalDriverIds.has(d.driverId)).length,
    'on-track': deliveries.filter(d => d.status === 'pending' && !criticalDriverIds.has(d.driverId)).length
  };
  const filtered = deliveries.filter(d => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = d.refNumber.toLowerCase().includes(q) || d.destination.toLowerCase().includes(q) || d.commodity.toLowerCase().includes(q) || d.driverName.toLowerCase().includes(q) || d.truckId.toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (filter === 'all') return true;
    if (filter === 'at-risk') return d.status === 'at-risk' && !criticalDriverIds.has(d.driverId);
    if (filter === 'critical') return criticalDriverIds.has(d.driverId);
    if (filter === 'on-track') return d.status === 'pending' && !criticalDriverIds.has(d.driverId);
    return true;
  });
  const sorted = [...filtered].sort((a, b) => {
    const getPriority = (d: Delivery) => {
      if (criticalDriverIds.has(d.driverId) && d.status !== 'completed') return 0;
      if (d.status === 'at-risk') return 1;
      if (d.status === 'pending') return 2;
      return 3;
    };
    return getPriority(a) - getPriority(b);
  });
  const completedCount = deliveries.filter(d => d.status === 'completed').length;
  const atRiskCount = deliveries.filter(d => d.status === 'at-risk').length;
  return <div className="flex flex-col h-full" data-id="element-63">
      {/* Search & Filter */}
      <div className="p-3 border-b border-slate-800 space-y-3 shrink-0" data-id="element-64">
        {showFilters && <div className="flex gap-1 bg-slate-900 p-1 rounded-lg border border-slate-800" data-id="element-65">
            <FilterTab active={filter === 'all'} onClick={() => setFilter('all')} label="All" count={statusCounts.all} data-id="element-66" />
            <FilterTab active={filter === 'at-risk'} onClick={() => setFilter('at-risk')} label="At Risk" count={statusCounts['at-risk']} data-id="element-67" />
            <FilterTab active={filter === 'critical'} onClick={() => setFilter('critical')} label="Critical" count={statusCounts.critical} data-id="element-68" />
            <FilterTab active={filter === 'on-track'} onClick={() => setFilter('on-track')} label="On Track" count={statusCounts['on-track']} data-id="element-69" />
          </div>}
        <div className="relative" data-id="element-70">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-71" />
          <input type="text" placeholder="Search ref #, address, cargo..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2 text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-slate-600 transition-colors" data-id="element-72" />
        </div>
      </div>

      {/* Delivery List */}
      <div className="flex-1 overflow-y-auto p-3 scrollbar-hide" data-id="element-73">
        {sorted.length === 0 && <div className="text-center p-8 text-slate-500 text-sm" data-id="element-74">
            No deliveries match your search.
          </div>}
        {sorted.map(delivery => {
        const isCriticalDriver = criticalDriverIds.has(delivery.driverId);
        const statusConfig = isCriticalDriver && delivery.status !== 'completed' ? {
          badge: 'text-brand-red bg-brand-red/10 border-brand-red/30',
          label: 'CRITICAL'
        } : getDeliveryStatusConfig(delivery.status);
        const isDriverSelected = delivery.driverId === selectedDriverId;
        return <div key={delivery.id} onClick={() => onSelectDriver(delivery.driverId, delivery.refNumber)} className={`relative p-3 mb-2 last:mb-0 rounded-xl border cursor-pointer transition-all
                ${isDriverSelected ? 'bg-slate-800/60 border-slate-600 shadow-[0_0_15px_rgba(0,0,0,0.2)]' : 'bg-slate-900/50 border-slate-800 hover:border-slate-700'}`} data-id="element-75">
              {/* Row 1: Ref # + Status badge */}
              <div className="flex items-center justify-between mb-1.5" data-id="element-76">
                <span className={`text-sm font-bold font-mono ${isDriverSelected ? 'text-slate-100' : 'text-slate-200'}`} data-id="element-77">
                  {delivery.refNumber}
                </span>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${statusConfig.badge}`} data-id="element-78">
                  {statusConfig.label}
                </span>
              </div>

              {/* Row 2: Destination */}
              <p className={`text-xs mb-1.5 ${delivery.status === 'completed' ? 'text-slate-500 line-through' : 'text-slate-300'}`} data-id="element-79">
                {delivery.destination}
              </p>

              {/* Row 3: Contents */}
              <div className="flex items-center gap-1.5 mb-2" data-id="element-80">
                <PackageIcon className="w-3 h-3 text-slate-600" data-id="element-81" />
                <span className="text-[11px] text-slate-400 truncate" data-id="element-82">
                  {delivery.commodity}
                </span>
                <span className="text-[10px] text-slate-600" data-id="element-83">·</span>
                <span className="text-[10px] text-slate-500 shrink-0" data-id="element-84">
                  {delivery.pallets}p / {(delivery.weight / 1000).toFixed(1)}k
                  lbs
                </span>
              </div>

              {/* Row 4: ETA window */}
              <div className="flex items-center gap-2 mb-2" data-id="element-85">
                <span className={`text-[10px] font-mono ${isCriticalDriver && delivery.status !== 'completed' ? 'text-brand-red font-semibold' : delivery.status === 'at-risk' ? 'text-brand-amber font-semibold' : 'text-slate-500'}`} data-id="element-86">
                  ETA {delivery.eta}
                </span>
                <span className="text-[10px] text-slate-600" data-id="element-87">·</span>
                <span className="text-[10px] text-slate-500" data-id="element-88">
                  Window {delivery.window}
                </span>
              </div>

              {/* Row 5: Driver + Truck */}
              <div className="flex items-center justify-between" data-id="element-89">
                <span className="text-[10px] text-slate-500" data-id="element-90">
                  {delivery.driverName} ·{' '}
                  <span className="font-mono" data-id="element-91">{delivery.truckId}</span>
                </span>
                <ChevronRight className={`w-3.5 h-3.5 ${isDriverSelected ? 'text-slate-400' : 'text-slate-600'}`} data-id="element-92" />
              </div>
            </div>;
      })}
      </div>
    </div>;
}
function FilterTab({
  active,
  onClick,
  label,
  count
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}) {
  return <button onClick={onClick} className={`flex-1 text-[10px] py-1.5 rounded-md transition-colors font-medium flex items-center justify-center gap-1.5
        ${active ? 'bg-slate-800 text-slate-100 shadow-sm' : 'text-slate-500 hover:text-slate-300'}`} data-id="element-93">
      {label}
      {count !== undefined && <span className={`min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold flex items-center justify-center ${active ? 'bg-slate-700 text-slate-300' : 'bg-slate-800 text-slate-500'}`} data-id="element-94">
          {count}
        </span>}
    </button>;
}
function getDeliveryStatusConfig(status: 'completed' | 'pending' | 'at-risk') {
  switch (status) {
    case 'completed':
      return {
        badge: 'text-brand-green bg-brand-green/10 border-brand-green/30',
        label: 'DELIVERED'
      };
    case 'at-risk':
      return {
        badge: 'text-brand-amber bg-brand-amber/10 border-brand-amber/30',
        label: 'AT RISK'
      };
    case 'pending':
      return {
        badge: 'text-slate-400 bg-slate-800 border-slate-700',
        label: 'PENDING'
      };
  }
}